/**
 * EO «Аудит» — лог действий сотрудников (V1 AuditView, index.html:15300-15510
 * → V4). Период — DateSelector range в шапке EoScreen + пресеты (Сегодня/
 * Вчера/7 дней/30 дней, UTC+5). Фильтры: сотрудник (id точно или имя LIKE),
 * действие (LIKE по action/detail), тип сущности (wire `entity_type`),
 * тумблер ⚠ attention. Сворачиваемая сводка (по сотрудникам / по действиям,
 * клик ставит фильтр), баннер «требуют внимания», лента по 50 с «Ещё».
 * created_at — UTC без Z → parse+5ч на клиенте. Read-only (perm audit:read).
 */
import { useMemo, useState } from "react";
import { useAudit, type AuditRow } from "../../hooks/useAudit";
import { ACTION_LABELS, ENTITY_LABELS, actionAccent, type AuditAccent } from "./auditLabels";
import { addDays, fmtDateApi, todayBusiness } from "../../utils/date";
import css from "./audit.module.css";

/** UTC sqlite «YYYY-MM-DD HH:MM:SS» (без Z) → {time, date} в UTC+5. */
function fmtAuditTs(s: string): { time: string; date: string } {
  const d = new Date(s.includes("T") ? s : `${s.replace(" ", "T")}Z`);
  if (Number.isNaN(d.getTime())) return { time: "—", date: "" };
  const b = new Date(d.getTime() + 5 * 3600 * 1000);
  const p = (n: number) => String(n).padStart(2, "0");
  return {
    time: `${p(b.getUTCHours())}:${p(b.getUTCMinutes())}`,
    date: `${p(b.getUTCDate())}.${p(b.getUTCMonth() + 1)}`,
  };
}

const ACCENT_CLS: Record<AuditAccent, string | undefined> = {
  green: css.accGreen, cyan: css.accCyan, red: css.accRed, orange: css.accOrange,
  purple: css.accPurple, gold: css.accGold, pink: css.accPink, muted: css.accMuted,
  default: undefined,
};

function LogRow({ a, onStaff }: { a: AuditRow; onStaff: (name: string) => void }) {
  const { time, date } = fmtAuditTs(a.created_at);
  const accent = ACCENT_CLS[actionAccent(a.action)];
  return (
    <div className={`${css.row} ${a.attention ? css.rowAttn : ""}`}>
      <div className={css.tCol}>
        <div className={css.tTime}>{time}</div>
        <div className={css.tDate}>{date}</div>
      </div>
      <div className={css.main}>
        <div className={css.headLine}>
          <button type="button" className={css.name} onClick={() => onStaff(a.staff_name ?? "")}>
            {a.staff_name || "система"}
          </button>
          <span className={`${css.badge} ${accent ?? ""}`}>{ACTION_LABELS[a.action] ?? a.action}</span>
        </div>
        {a.detail ? <div className={css.detail}>{a.detail}</div> : null}
        {a.entity_type ? (
          <div className={css.entity}>{ENTITY_LABELS[a.entity_type] ?? a.entity_type} #{a.entity_id}</div>
        ) : null}
      </div>
    </div>
  );
}

export function EoAuditView({ from, to, onSetRange }: {
  from: string; to: string; onSetRange: (from: string, to: string) => void;
}) {
  const audit = useAudit(from, to);
  const [showStats, setShowStats] = useState(true);
  const { filters, setFilters, stats } = audit;

  const presets = useMemo(() => {
    const t = todayBusiness();
    const f = fmtDateApi;
    return [
      { label: "Сегодня", from: f(t), to: f(t) },
      { label: "Вчера", from: f(addDays(t, -1)), to: f(addDays(t, -1)) },
      { label: "7 дней", from: f(addDays(t, -6)), to: f(t) },
      { label: "30 дней", from: f(addDays(t, -29)), to: f(t) },
    ];
  }, []);

  return (
    <div className={css.wrap}>
      <div className={css.presetRow}>
        {presets.map((p) => (
          <button
            key={p.label} type="button"
            className={`${css.pchip} ${from === p.from && to === p.to ? css.pchipOn : ""}`}
            onClick={() => onSetRange(p.from, p.to)}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className={css.filterRow}>
        <input
          className={css.inp} value={filters.staff} placeholder="Сотрудник…"
          onChange={(e) => setFilters({ staff: e.target.value })}
        />
        <input
          className={css.inp} value={filters.action} placeholder="Действие…"
          onChange={(e) => setFilters({ action: e.target.value })}
        />
        <select className={css.sel} value={filters.entity} onChange={(e) => setFilters({ entity: e.target.value })}>
          <option value="">Все типы</option>
          {Object.entries(ENTITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <button
          type="button" title="Только требующие внимания"
          className={`${css.warnBtn} ${filters.attentionOnly ? css.warnOn : ""}`}
          onClick={() => setFilters({ attentionOnly: !filters.attentionOnly })}
        >
          ⚠
        </button>
      </div>

      {stats ? (
        <div>
          <button type="button" className={css.statsHead} onClick={() => setShowStats((v) => !v)}>
            Сводка {showStats ? "▾" : "▸"}
          </button>
          {showStats ? (
            <div className={css.statsGrid}>
              <div className={css.statsCard}>
                <div className={`${css.statsTitle} ${css.accPurple}`}>ПО СОТРУДНИКАМ</div>
                {stats.byStaff.slice(0, 8).map((s, i) => (
                  <div key={`${s.staff_id ?? i}`} className={css.statsRow}>
                    <button type="button" className={css.statsName} onClick={() => setFilters({ staff: s.staff_name ?? "" })}>
                      {s.staff_name || "—"}
                    </button>
                    <span className={css.statsCnt}>{s.cnt}</span>
                  </div>
                ))}
                {stats.byStaff.length === 0 ? <div className={css.statsEmpty}>пусто</div> : null}
              </div>
              <div className={css.statsCard}>
                <div className={`${css.statsTitle} ${css.accCyan}`}>ПО ДЕЙСТВИЯМ</div>
                {stats.byAction.slice(0, 8).map((a) => (
                  <div key={a.action} className={css.statsRow}>
                    <button
                      type="button"
                      className={`${css.statsName} ${ACCENT_CLS[actionAccent(a.action)] ?? ""}`}
                      onClick={() => setFilters({ action: a.action })}
                    >
                      {ACTION_LABELS[a.action] ?? a.action}
                    </button>
                    <span className={css.statsCnt}>{a.cnt}</span>
                  </div>
                ))}
                {stats.byAction.length === 0 ? <div className={css.statsEmpty}>пусто</div> : null}
              </div>
            </div>
          ) : null}
          {stats.attention > 0 ? (
            <button type="button" className={css.attnBanner} onClick={() => setFilters({ attentionOnly: true })}>
              ⚠ {stats.attention} записей требуют внимания
            </button>
          ) : null}
        </div>
      ) : null}

      <div className={css.count}>Записей: {audit.total}</div>

      {audit.loading ? (
        <div className={css.skel}><div className={css.skRow} /><div className={css.skRow} /><div className={css.skRow} /></div>
      ) : audit.error ? (
        <div className={css.empty}>
          Не удалось загрузить аудит: {audit.error}<br />
          <button type="button" className={css.retry} onClick={audit.reload}>Повторить</button>
        </div>
      ) : audit.log.length === 0 ? (
        <div className={css.empty}>Записей по фильтру нет</div>
      ) : (
        <>
          <div className={css.feed}>
            {audit.log.map((a) => (
              <LogRow key={a.id} a={a} onStaff={(name) => setFilters({ staff: name })} />
            ))}
          </div>
          {audit.log.length < audit.total ? (
            <button type="button" className={css.more} onClick={audit.loadMore} disabled={audit.loadingMore}>
              {audit.loadingMore
                ? "Загрузка…"
                : `Ещё ${Math.min(50, audit.total - audit.log.length)} из ${audit.total - audit.log.length}`}
            </button>
          ) : null}
        </>
      )}
    </div>
  );
}
